import { FACADE_SERVICE_LABEL, FACADE_SERVICE_SLUG, includesServiceSlug, resolveSlug } from "@/config/serviceCatalog";
import { getServiceModule } from "./registry";
import type { ServiceModule } from "./types";

/**
 * Available plugins — each maps to a service slug that has a custom ServiceModule.
 * Admin toggles these per supplier via `enabled_plugins`.
 */
export const AVAILABLE_PLUGINS: { slug: string; label: string }[] = [
  { slug: FACADE_SERVICE_SLUG, label: FACADE_SERVICE_LABEL },
];

/**
 * Runtime plugin resolution rule:
 *
 * Varianta A: service ∈ enabled_services, service ∉ enabled_plugins → generic flow
 * Varianta B: service ∈ enabled_services, service ∈ enabled_plugins → plugin flow
 * Varianta C: service ∉ enabled_services, service ∈ enabled_plugins → plugin BLOCKED
 *
 * Returns the ServiceModule only if both conditions are met (Varianta B).
 * Otherwise returns null → caller uses generic flow.
 */
export async function resolvePlugin(
  engineId: string | null | undefined,
  supplierEnabledServices: string[],
  supplierEnabledPlugins: string[],
): Promise<ServiceModule | null> {
  if (!engineId) return null;
  const normalizedEngineId = resolveSlug(engineId);

  // Rule: service must be in enabled_services
  if (!includesServiceSlug(supplierEnabledServices, normalizedEngineId)) return null;

  // Rule: plugin must be in enabled_plugins
  if (!includesServiceSlug(supplierEnabledPlugins, normalizedEngineId)) return null;

  // Both conditions met — load the plugin module
  return getServiceModule(normalizedEngineId);
}

/**
 * Services that are ALWAYS plugin-only — they never show in generic catalog.
 * Even if the plugin is not enabled for a supplier, the service should not
 * fall back to generic pricing. It either runs as plugin or not at all.
 */
const PLUGIN_ONLY_SLUGS = new Set(AVAILABLE_PLUGINS.map(p => p.slug));

export function isPluginOnlyService(slug: string): boolean {
  return PLUGIN_ONLY_SLUGS.has(resolveSlug(slug));
}

/**
 * Synchronous check: should we use plugin flow for this supplier + service?
 */
export function hasPlugin(
  engineId: string | null | undefined,
  supplierEnabledServices: string[],
  supplierEnabledPlugins: string[],
): boolean {
  if (!engineId) return false;
  const normalizedEngineId = resolveSlug(engineId);
  return (
    includesServiceSlug(supplierEnabledServices, normalizedEngineId) &&
    includesServiceSlug(supplierEnabledPlugins, normalizedEngineId)
  );
}
